import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import styles from "./PaymentAndTax.module.scss";
import SettingsCard from "../SettingsCard/SettingsCard";
import SettingsField from "../SettingsField/SettingsField";
import SettingsInput from "../SettingsInput/SettingsInput";
import SettingsService from "@/services/api/settings";
import toast from "@/lib/toast";
import { floatingPointNumberRegex } from "@/lib/regex";

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

interface TaxConfigFormState {
  gstRate: string;
  gstin: string;
}

interface FormErrors {
  gstRate?: string;
  gstin?: string;
}

const EMPTY_FORM: TaxConfigFormState = { gstRate: "", gstin: "" };

export interface PaymentAndTaxHandle {
  save: () => Promise<void>;
  cancel: () => void;
}

export interface PaymentAndTaxProps {
  onSavingChange?: (isSaving: boolean) => void;
}

const PaymentAndTax = forwardRef<PaymentAndTaxHandle, PaymentAndTaxProps>(
  ({ onSavingChange }, ref) => {
    const [formData, setFormData] = useState<TaxConfigFormState>(EMPTY_FORM);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const fetchTaxSettings = useCallback(async () => {
      setIsLoading(true);
      try {
        const data = await SettingsService.getPaymentTaxSettings();
        setFormData({
          gstRate: data.taxPercentage != null ? `${data.taxPercentage}%` : "",
          gstin: data.gstin ?? "",
        });
      } catch {
        // privateRequest's response interceptor already shows an error toast
      } finally {
        setIsLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchTaxSettings();
    }, [fetchTaxSettings]);

    const handleFieldChange = (field: keyof TaxConfigFormState, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const validate = (): boolean => {
      const nextErrors: FormErrors = {};

      const rawRate = formData.gstRate.replace(/%/g, "").trim();
      if (!rawRate) {
        nextErrors.gstRate = "GST Rate is required";
      } else if (
        !floatingPointNumberRegex.test(rawRate) ||
        Number(rawRate) < 0 ||
        Number(rawRate) > 100
      ) {
        nextErrors.gstRate = "Enter a valid GST rate between 0 and 100";
      }

      const rawGstin = formData.gstin.trim().toUpperCase();
      if (!rawGstin) {
        nextErrors.gstin = "GSTIN is required";
      } else if (!GSTIN_REGEX.test(rawGstin)) {
        nextErrors.gstin = "Enter a valid GSTIN";
      }

      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    };

    const save = useCallback(async () => {
      if (isSaving) return;
      if (!validate()) return;

      setIsSaving(true);
      onSavingChange?.(true);
      try {
        await SettingsService.updatePaymentTaxSettings({
          gstin: formData.gstin.trim().toUpperCase(),
          taxPercentage: Number(formData.gstRate.replace(/%/g, "").trim()),
        });
        toast.success("Payment & tax settings updated successfully");
        await fetchTaxSettings();
      } catch {
        // privateRequest's response interceptor already shows an error toast
      } finally {
        setIsSaving(false);
        onSavingChange?.(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isSaving, onSavingChange, fetchTaxSettings]);

    const cancel = useCallback(() => {
      setErrors({});
      fetchTaxSettings();
    }, [fetchTaxSettings]);

    useImperativeHandle(ref, () => ({ save, cancel }), [save, cancel]);

    return (
      <div className={styles.container}>
        <SettingsCard title="Tax Configuration">
          {isLoading ? (
            <p className={styles.loadingText}>Loading tax configuration...</p>
          ) : (
            <div className={styles.taxGrid}>
              <SettingsField label="GST RATE" htmlFor="gstRate">
                <SettingsInput
                  id="gstRate"
                  type="text"
                  value={formData.gstRate}
                  onChange={(e) => handleFieldChange("gstRate", e.target.value)}
                  placeholder="e.g. 18%"
                  aria-label="GST Rate"
                  disabled={isSaving}
                />
                {errors.gstRate && <span className={styles.errorText}>{errors.gstRate}</span>}
              </SettingsField>

              <SettingsField label="GSTIN" htmlFor="gstin">
                <SettingsInput
                  id="gstin"
                  type="text"
                  value={formData.gstin}
                  onChange={(e) => handleFieldChange("gstin", e.target.value)}
                  placeholder="e.g. 27AADCT1234F1ZH"
                  aria-label="GSTIN"
                  disabled={isSaving}
                />
                {errors.gstin && <span className={styles.errorText}>{errors.gstin}</span>}
              </SettingsField>
            </div>
          )}
        </SettingsCard>
      </div>
    );
  },
);

PaymentAndTax.displayName = "PaymentAndTax";

export default PaymentAndTax;
